import { useFormData } from "../../Hooks/useFormDataContext";
import axios from "../../api/axios";
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from "react";

// ── ProfileForm (RegistrationType === 0) ────────────────────────────────────
// This used to be "SaveAllButton": it rendered its own "Save and Register"
// button and, on click, ran the ENTIRE flow itself (create attendee → create
// order → create order items → patch attendee → navigate). That made it
// impossible to combine with AttendeeForm-based (Type 1) registrations on the
// same page, since each button drove its own separate order.
//
// It's now a ref-driven component with the SAME shape as AttendeeForm:
//   useImperativeHandle(ref, () => ({ submit, canSubmit }))
// submit() only creates the attendee record and returns
//   { attendee, registration, insuranceCost }
// Order creation, order items, and navigation all happen once, centrally, in
// Register.js's handleSubmitAll() — after every selected registration
// (profile-based and/or form-based) has been submitted.
//
// Insurance is also calculated ONCE, centrally, in Register.js and passed
// down as the InsuranceCost prop — this component no longer fetches the
// insurance option or recomputes the day-based rate itself.
const ProfileForm = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const { formData, updateForm, resetForm, initForm } = useFormData();
  const { enqueueSnackbar } = useSnackbar();
  const user = props.User;
  const registration = props.Registration;
  const event = props.Event;
  const team = props.Team;
  const globalIndex = props.globalIndex ?? 0;
  const onReadyChange = props.onReadyChange;
  const insuranceCost = props.InsuranceCost ?? 0;

  // The logged-in user's id. Accepts either a flat user ({ UserID }) or a
  // wrapped one ({ user: { UserID } }) so this never ends up undefined just
  // because of the prop's shape.
  const currentUserId = user?.UserID ?? user?.user?.UserID;

  // Keep a ref that always reflects the latest attendeeDates prop
  const attendeeDatesRef = useRef(props.AttendeeDates || { arrivalDate: null, departureDate: null });

  useEffect(() => {
    attendeeDatesRef.current = props.AttendeeDates || { arrivalDate: null, departureDate: null };
  }, [props.AttendeeDates]);

  useEffect(() => {
    if (currentUserId) {
      initForm("User", { UserID: parseInt(currentUserId) });
    }
  }, [currentUserId]);

  //datetime helper
  const toISOSafe = (val) => {
    if (!val) return null;
    if (DateTime.isDateTime(val)) return val.toISODate();
    if (val instanceof Date) return DateTime.fromJSDate(val).toISODate();
    if (typeof val === 'string') return val.slice(0, 10);
    return null;
  };

  // ── Ready state ───────────────────────────────────────────────────────────
  // NOTE: ProfileSections manages the profile field UI itself and isn't shown
  // here, so this component can't independently verify "all required fields
  // filled" the way AttendeeForm does. It reports ready=true once mounted,
  // matching the original SaveAllButton's always-clickable behavior. If
  // ProfileSections exposes its own validity (e.g. via the shared formData
  // context or a callback prop), wire that into onReadyChange here instead of
  // the hardcoded `true` so the shared submit button can't fire early.
  useEffect(() => {
    onReadyChange?.(globalIndex, true);
  }, [globalIndex, onReadyChange]);

  const createAttendee = async (resolvedUser, groupOverride) => {
    // Prefer the id on the resolved user, but always fall back to the
    // logged-in user's id. The usersettings endpoint returns the settings row
    // (no UserID), which previously produced attendees with no user attached.
    const attendeeUserId = resolvedUser?.UserID ?? currentUserId;

    if (!attendeeUserId) {
      console.error('Cannot create attendee: no UserID could be resolved', { resolvedUser, user });
      enqueueSnackbar('Unable to determine your user account. Please refresh and try again.', { variant: 'error' });
      return null;
    }

    // Read from ref so we always get the latest value, not the stale closure value
    const currentDates = attendeeDatesRef.current;

    try {
      const payload = {
        BPEventID: Number(event.EventsID),
        BPAttendeeUserID: Number(attendeeUserId),
        BPAttendeeType: registration.RegistrationName,
        BPAttendeeLabel: registration.SectionLabel,
        BPRegistrationID: registration.RegistrationID,
        BPAttendeeBalance: parseFloat(registration.RegistrationCost) + insuranceCost,
        BPAttendeeStatus: registration.RequireApproval ? "Pending" : "Approved",
        BPAttendeeApproved: !registration.RequireApproval,
        // Group is always attached if present — not gated behind BP_FlexibleDates.
        // Prefer the group Register.js just created/resolved for THIS submission
        // (state `team` is stale during the same submit cycle).
        ...((groupOverride ?? team) && { BPAttendeeGroup: (groupOverride ?? team).BPGroupName }),
        ...(event.BP_DatesFlexible && {
          BPAttendeeArrivalDate: toISOSafe(currentDates.arrivalDate),
          BPAttendeeDepartureDate: toISOSafe(currentDates.departureDate),
        }),
      };

      const res = await axios.post('/api/attendees', payload);
      const newAttendee = res.data;
      //console.log('Attendee Obj: ', newAttendee);
      if (!newAttendee?.BPAttendeeID) throw new Error('No BPAttendeeID returned');
      return newAttendee;
    } catch (e) {
      if (e?.response?.status === 409) {
        enqueueSnackbar('Attendee already exists for this user', { variant: 'warning' });
        navigate('/events/error');
        return null;
      }
      console.error('Attendee creation failed:', e);
      enqueueSnackbar(e?.response?.data?.error || 'Failed to create attendee', { variant: 'error' });
      return null;
    }
  };

  // ── submit() — called by Register.js's handleSubmitAll() ──────────────────
  // Mirrors AttendeeForm.submit(): saves any changed profile/user data, then
  // creates the attendee record and hands back exactly what the centralized
  // order-creation step needs. Does NOT create an order, create order items,
  // patch the attendee with an OrderID, or navigate — that all happens once,
  // centrally, after every selected registration has submitted.
  async function submit(groupOverride) {
    const { User, UserSettings } = formData;
    const userEmpty = !User || Object.keys(User).filter(k => k !== 'UserID').length === 0;
    const isEmpty = (obj) => !obj || Object.keys(obj).length === 0;
    const userSettingsEmpty = isEmpty(UserSettings);

    try {
      // ── 1. Save user info if anything changed ──────────────────────────
      let resolvedUser = user;
      if (!userEmpty || !userSettingsEmpty) {
        let res;
        if (!userEmpty && userSettingsEmpty) {
          res = await axios.put(`/api/users/${currentUserId}`, User);
        } else if (userEmpty && !userSettingsEmpty) {
          res = await axios.put(`/api/users/usersettings/${currentUserId}`, UserSettings);
        } else {
          res = await axios.put('/api/users/alluserinfo', {
            user: !userEmpty ? User : null,
            userSettings: !userSettingsEmpty ? UserSettings : null,
          });
        }

        // Only adopt the response as the user if it actually IS a user.
        // The usersettings endpoint returns the settings row, which has
        // UserSettingsUserID but no UserID — using it as the user is what
        // caused attendees to be created without a user attached.
        const saved = res.data?.user ?? res.data;
        if (saved?.UserID) {
          resolvedUser = saved;
        }
        // console.log('User info saved:', resolvedUser);
      }

      // ── 2. Create attendee ───────────────────────────────────────────────
      const newAttendee = await createAttendee(resolvedUser, groupOverride);
      if (!newAttendee) return null; // createAttendee already handled its own snackbar/nav

      //console.log('Attendee created:', newAttendee);
      resetForm();

      return { attendee: newAttendee, registration, insuranceCost };
    } catch (ex) {
      console.error('Save failed:', ex);
      enqueueSnackbar('Saving failed: ' + (ex.response?.data?.error || ex.message), { variant: 'error' });
      return null;
    }
  }

  useImperativeHandle(ref, () => ({ submit, canSubmit: true }));

  // No visible UI of its own anymore — ProfileSections (rendered by the
  // parent alongside this component) is the form the user interacts with.
  // Submission is triggered exclusively by Register.js's single shared button.
  return null;
});

export default ProfileForm;